import type { TaskPaneCollectionProps } from './components/TaskPaneCollection';
import type { AddinsFlexPaneBodyProps } from './components/AddinsFlexPaneBody';
import type { AddinsPaneHeaderCollectionProps } from './components/AddinsFlexPaneHeader';
import type { CopilotAppViewProps } from './components/CopilotAppView';
import type * as lazyIndex from './lazyIndex';
import { LazyAction, type LazyComponent, LazyModule, createLazyComponent } from 'owa-bundling';

const lazyModule: LazyModule<typeof lazyIndex> = new LazyModule<typeof lazyIndex>(
    () => import(/* webpackChunkName: "AddinsView" */ './lazyIndex'),
    { name: 'AddinsView' }
);

export const CopilotAppView: LazyComponent<CopilotAppViewProps, unknown, void> =
    createLazyComponent(lazyModule, m => m.CopilotAppView);

export const lazyCreateAddinCommandSurfaceAction: LazyAction<
    typeof lazyIndex.createAddinCommandSurfaceAction,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.createAddinCommandSurfaceAction);
export const lazyOnSendAddinItemSwitch: LazyAction<
    typeof lazyIndex.onSendAddinItemSwitch,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.onSendAddinItemSwitch);
export const lazyOnLaunchEventTriggered: LazyAction<
    typeof lazyIndex.onLaunchEventTriggered,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.onLaunchEventTriggered);
export const lazyOnHostItemChanged: LazyAction<
    typeof lazyIndex.onHostItemChanged,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.onHostItemChanged);

export const lazyChildWindowMessageListener: LazyAction<
    typeof lazyIndex.childWindowMessageListener,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.childWindowMessageListener);
export const AddinsFlexPaneHeader: LazyComponent<AddinsPaneHeaderCollectionProps, unknown, void> =
    createLazyComponent(lazyModule, m => m.AddinsFlexPaneHeader);
export const AddinsFlexPaneBody: LazyComponent<AddinsFlexPaneBodyProps, unknown, void> =
    createLazyComponent(lazyModule, m => m.AddinsFlexPaneBody);
export const TaskPaneCollection: LazyComponent<TaskPaneCollectionProps, unknown, void> =
    createLazyComponent(lazyModule, m => m.TaskPaneCollection);
export const lazyOnItemNavigation: LazyAction<
    typeof lazyIndex.onItemNavigation,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.onItemNavigation);
export const lazyAllowItemSendAndBlockOnSendAddinEvents: LazyAction<
    typeof lazyIndex.allowItemSendAndBlockOnSendAddinEvents,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.allowItemSendAndBlockOnSendAddinEvents);
export const lazyOnComposeClose: LazyAction<typeof lazyIndex.onComposeClose, typeof lazyModule> =
    new LazyAction(lazyModule, m => m.onComposeClose);
export const lazyShowOfflineModeDialog: LazyAction<
    typeof lazyIndex.showOfflineModeDialog,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.showOfflineModeDialog);
export const lazyCloseTaskPaneAddinCommand: LazyAction<
    typeof lazyIndex.closeTaskPaneAddinCommand,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.closeTaskPaneAddinCommand);
export const lazyOpenContextualCallout: LazyAction<
    typeof lazyIndex.openContextualCallout,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.openContextualCallout);
export const lazyLaunchAddinCommand: LazyAction<
    typeof lazyIndex.launchAddinCommand,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.launchAddinCommand);
export const lazyAddinCommandSurfaceItemsObserver: LazyAction<
    typeof lazyIndex.observeAddinCommandSurfaceItems,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.observeAddinCommandSurfaceItems);
export const lazyInitializeExecuteEntryPoint: LazyAction<
    typeof lazyIndex.ExecuteEntryPoint,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.ExecuteEntryPoint);
export const lazyRemoveUninstalledIntegratedSpamAddinEntriesFromLocalStorage: LazyAction<
    typeof lazyIndex.removeUninstalledIntegratedSpamAddinEntriesFromLocalStorage,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.removeUninstalledIntegratedSpamAddinEntriesFromLocalStorage);
export const lazySetSoftBlockDialogState: LazyAction<
    typeof lazyIndex.setSoftBlockDialogState,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.setSoftBlockDialogState);
export const lazyInitializeExtensibilityFrameworkComponent: LazyAction<
    typeof lazyIndex.initializeExtensibilityFrameworkComponent,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.initializeExtensibilityFrameworkComponent);
export const lazyRemoveExtensibilityFrameworkComponent: LazyAction<
    typeof lazyIndex.removeExtensibilityFrameworkComponent,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.removeExtensibilityFrameworkComponent);
export const lazyOpenNonPersistentTaskPaneAddinCommand: LazyAction<
    typeof lazyIndex.openNonPersistentTaskPaneAddinCommand,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.openNonPersistentTaskPaneAddinCommand);
export const lazyOpenPersistentTaskPaneAddinCommand: LazyAction<
    typeof lazyIndex.openPersistentTaskPaneAddinCommand,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.openPersistentTaskPaneAddinCommand);
export const lazyLaunchAutoRunAddinsById: LazyAction<
    typeof lazyIndex.launchAutoRunAddinsById,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.launchAutoRunAddinsById);
export const lazyCheckAdminPolicyAndOfflineAddins: LazyAction<
    typeof lazyIndex.checkAdminPolicyAndOfflineAddins,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.checkAdminPolicyAndOfflineAddins);
export const lazyGetAddinCommandSurfaceItems: LazyAction<
    typeof lazyIndex.getAddinCommandSurfaceItems,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.getAddinCommandSurfaceItems);
export const lazyUpdatedAddinSurfaceCache: LazyAction<
    typeof lazyIndex.updatedAddinSurfaceCache,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.updatedAddinSurfaceCache);
export const lazyCheckBlockSendByAddinForSdk: LazyAction<
    typeof lazyIndex.checkBlockSendByAddinForSdk,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.checkBlockSendByAddinForSdk);
